export type ConsentCategory = 'functional' | 'analytics' | 'ads';

export interface IConsentState {
    version: number;
    functional: boolean;
    analytics: boolean;
    ads: boolean;
    timestamp: number;
}

type ConsentListener = (consent: IConsentState) => void;

export const CONSENT_COOKIE_NAME = 'epk_cookie_consent';
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 182;

class ConsentManager {

    private listeners: ConsentListener[] = [];

    public read(): IConsentState | null {
        if (typeof document === 'undefined') {
            return null;
        }
        const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + CONSENT_COOKIE_NAME + '=([^;]*)'));
        if (!match) {
            return null;
        }
        try {
            const parsed: IConsentState = JSON.parse(decodeURIComponent(match[1]));
            if (parsed.version !== CONSENT_VERSION) {
                return null;
            }
            return parsed;
        } catch (e) {
            return null;
        }
    }

    public has(category: ConsentCategory): boolean {
        if (category === 'functional') {
            return true;
        }
        const consent = this.read();
        return !!(consent && consent[category] === true);
    }

    public save(choices: { analytics?: boolean, ads?: boolean }): IConsentState {
        const consent: IConsentState = {
            version: CONSENT_VERSION,
            functional: true,
            analytics: choices.analytics === true,
            ads: choices.ads === true,
            timestamp: Date.now()
        };
        if (typeof document !== 'undefined') {
            document.cookie = CONSENT_COOKIE_NAME + '=' + encodeURIComponent(JSON.stringify(consent))
                + ';path=/;max-age=' + CONSENT_MAX_AGE_SECONDS + ';samesite=lax';
        }
        this.notify(consent);
        return consent;
    }

    public reset(): void {
        if (typeof document !== 'undefined') {
            document.cookie = CONSENT_COOKIE_NAME + '=;path=/;max-age=0';
        }
    }

    public onChange(listener: ConsentListener): () => void {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    private notify(consent: IConsentState): void {
        this.listeners.forEach((listener) => listener(consent));
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('epk-consent-changed', { detail: consent }));
        }
    }
}

export default new ConsentManager();